import { c as _c } from "react/compiler-runtime";
/**
 * FeatureBlock Component
 *
 * Displays an image with an overlapping text card. The layout alternates
 * between image-left/text-right and image-right/text-left based on the
 * `reverse` prop or automatic alternation via `index`.
 *
 * Design matches the SCW production site's feature sections:
 * - Image with subtle shadow
 * - Overlapping text card with orange left border
 * - Light gray background on text card
 * - Orange heading with gray body text
 *
 * @example
 * <FeatureBlock
 *   image="/images/feature.jpg"
 *   imageAlt="Multi-location viewing"
 *   title="Multi Location"
 *   description="View all your locations easily on one centralized platform."
 *   index={0}
 * />
 */

import Image from '@/components/media/StorefrontImage';
import { cn } from '@/lib/utils';
interface FeatureBlockProps {
  /** Image URL */
  image: string;
  /** Image alt text for accessibility */
  imageAlt: string;
  /** Orange heading in the text card */
  title: string;
  /** Body text - can be a string or React node for multiple paragraphs */
  description: string | React.ReactNode;
  /** Index for automatic alternation (even = image left, odd = image right) */
  index?: number;
  /** Override automatic alternation: true = image right, false = image left */
  reverse?: boolean;
  /** Additional CSS classes */
  className?: string;
}
export function FeatureBlock(t0) {
  const $ = _c(36);
  const {
    image,
    imageAlt,
    title,
    description,
    index: t1,
    reverse,
    className
  } = t0;
  const index = t1 === undefined ? 0 : t1;
  const isReversed = reverse !== undefined ? reverse : index % 2 === 1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("relative py-8 md:py-12", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isReversed && "lg:flex-row-reverse";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("flex flex-col gap-6", "lg:flex-row lg:items-center lg:gap-0", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isReversed ? "lg:pl-8" : "lg:pr-8";
  let t6;
  if ($[4] !== t5) {
    t6 = cn("relative w-full lg:w-[55%] flex-shrink-0", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== image || $[7] !== imageAlt) {
    t7 = <div className="relative h-[260px] sm:h-[320px] lg:h-[360px] xl:h-[400px] overflow-hidden rounded-sm bg-white shadow-lg"><Image src={image} alt={imageAlt} fill={true} className="object-contain" sizes="(max-width: 1024px) 100vw, 520px" /></div>;
    $[6] = image;
    $[7] = imageAlt;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = isReversed ? "lg:-mr-[6%]" : "lg:-ml-[6%]";
  let t10;
  if ($[12] !== t9) {
    t10 = cn("relative w-full lg:w-[50%]", t9, "lg:z-10 lg:my-auto");
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("p-6 md:p-8", "border-l-4 shadow-lg", "bg-white");
    t12 = {
      borderLeftColor: "var(--scw-orange)",
      backgroundColor: "var(--scw-gray-light)"
    };
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  let t13;
  if ($[16] !== title) {
    t13 = title && <h3 className="text-2xl md:text-3xl font-medium mb-4" style={{
      color: "var(--scw-orange)"
    }}>{title}</h3>;
    $[16] = title;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: "var(--scw-gray-body)"
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== description) {
    t15 = typeof description === "string" ? <p>{description}</p> : description;
    $[19] = description;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t15) {
    t16 = <div className="text-base md:text-lg leading-relaxed space-y-4" style={t14}>{t15}</div>;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t13 || $[24] !== t16) {
    t17 = <div className={t11} style={t12}>{t13}{t16}</div>;
    $[23] = t13;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t10 || $[27] !== t17) {
    t18 = <div className={t10}>{t17}</div>;
    $[26] = t10;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18 || $[30] !== t4 || $[31] !== t8) {
    t19 = <div className={t4}>{t8}{t18}</div>;
    $[29] = t18;
    $[30] = t4;
    $[31] = t8;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t19 || $[34] !== t2) {
    t20 = <div className={t2}>{t19}</div>;
    $[33] = t19;
    $[34] = t2;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
}